import { Product } from '@/types/product';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X, Star, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface LegacyComparisonGridProps {
  products: Product[];
}

/**
 * Legacy comparison grid (fallback when Circle Score and Promo Codes are disabled)
 */
export function LegacyComparisonGrid({ products }: LegacyComparisonGridProps) {
  const navigate = useNavigate();

  const extractFeatures = (product: Product) => {
    if (!product.features) return [];
    
    if (Array.isArray(product.features)) {
      return product.features.map((f: any) => {
        if (typeof f === 'string') return f;
        if (f.title) return f.title;
        return '';
      }).filter(Boolean);
    }
    
    return [];
  };

  const getAllFeatures = () => {
    const featureSet = new Set<string>();
    products.forEach(product => {
      extractFeatures(product).forEach(feature => featureSet.add(feature));
    });
    return Array.from(featureSet);
  };

  const hasFeature = (product: Product, featureName: string) => {
    const features = extractFeatures(product);
    return features.some(f => 
      f.toLowerCase().includes(featureName.toLowerCase()) || 
      featureName.toLowerCase().includes(f.toLowerCase())
    );
  };

  const allFeatures = getAllFeatures();

  return (
    <div className="overflow-x-auto">
      <div className="inline-block min-w-full align-middle">
        <div className="grid gap-4" style={{ gridTemplateColumns: `200px repeat(${products.length}, minmax(250px, 1fr))` }}>
          
          {/* Header Row - Product Cards */}
          <div className="font-semibold text-lg flex items-end pb-4">Features</div>
          {products.map((product) => (
            <Card key={product.id} className="h-full">
              <CardHeader className="space-y-4">
                {product.image && (
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-32 object-cover rounded-lg"
                  />
                )}
                <div>
                  <CardTitle className="text-lg mb-2">{product.title}</CardTitle>
                  {product.subtitle && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {product.subtitle}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  {product.rating && (
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold">{product.rating}</span>
                    </div>
                  )}
                  {product.price !== null && (
                    <div>
                      <span className="text-2xl font-bold text-primary">
                        ${product.price}
                      </span>
                      {product.price_duration && (
                        <span className="text-sm text-muted-foreground">
                          /{product.price_duration}
                        </span>
                      )}
                    </div>
                  )}
                  {Array.isArray(product.categories) && product.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {product.categories.slice(0, 2).map((cat: string, idx: number) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {cat}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <Button 
                  onClick={() => navigate(`/product/${product.slug}`)}
                  className="w-full"
                >
                  View Details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardHeader>
            </Card>
          ))}

          {/* Feature Comparison Rows */}
          {allFeatures.length > 0 ? (
            allFeatures.map((feature, idx) => (
              <div key={`feature-row-${idx}`} className="contents">
                <div 
                  className="py-4 px-2 border-t font-medium text-sm flex items-center"
                >
                  {feature}
                </div>
                {products.map((product) => (
                  <div 
                    key={`${product.id}-${idx}`}
                    className="py-4 px-4 border-t flex items-center justify-center"
                  >
                    {hasFeature(product, feature) ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <X className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div className="contents">
              <div className="py-8 text-center text-muted-foreground">
                No detailed features available for comparison
              </div>
              {products.map((product) => (
                <div key={product.id} className="py-8"></div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
